import { marked } from 'marked';
import { i18n } from '@/plugins/i18n';
import {
	SOCIAL_MEDIA_PLATFORMS_MAPPED,
} from '@/types/socialMedia';
import type {
	SingleExchangeAthlete,
	SingleExchangeAthleteLink,
} from './exchangeAthletes';
import {
	ATTESTATION_TYPES,
	type SocialMediaPlatformId,
	SOCIAL_MEDIA_PLATFORM_IDS,
	type Sport,
	type ApiV3,
} from '@teamworksdev/influencer-core';

type SingleExchangeSchoolAthleteLink = Pick<SingleExchangeAthleteLink, 'url' | 'platform'>;

type SingleExchangeSchoolSiteAthlete = Pick<SingleExchangeAthlete,
	'uuid' |
	'firstName' |
	'lastName' |
	'fullName' |
	'profileImage' |
	'rateCardsCount' |
	'teamName'
> & {
	socialProfiles: SingleExchangeSchoolAthleteLink[];
	links: SingleExchangeSchoolAthleteLink[];
};

class Attestation {
	isCheckbox: boolean;
	isHeading: boolean;
	isLink: boolean;
	text: string;
	url: string | undefined;

	constructor(data: ApiV3.Attestation) {
		this.isCheckbox = data.type === ATTESTATION_TYPES.CHECKBOX;
		this.isHeading = data.type === ATTESTATION_TYPES.HEADING;
		this.isLink = data.type === ATTESTATION_TYPES.LINK;
		this.text = data.text;
		this.url = data.url;
	}
}

class ExchangeSchoolSite {
	uuid: string;
	schoolName: string;
	exchangeLogo: string;
	exchangeTitle: string;
	exchangeFormalTitle: string;
	learnMoreText: string;
	headerBackground: string;
	primaryColor: string;
	backgroundColor: string;
	headerBackgroundColor: string;
	requiresApproval: boolean;
	causes: ApiV3.ExchangeSchoolSiteCause[];
	publish: boolean;
	sports: Sport[];
	attestations: Attestation[];

	constructor(data: ApiV3.ExchangeSchoolSite) {
		this.uuid = data.uuid;
		this.schoolName = data.name;
		this.exchangeLogo = data.local_exchange_logo;
		this.exchangeTitle = `${data.branding.title} ${i18n.global.t('exchange')}`;
		this.exchangeFormalTitle = i18n.global.t('exchangeSchoolSite.formalTitle', {
			exchangeTitle: this.exchangeTitle,
		});
		this.learnMoreText = marked(data.branding.body_text) as string;
		this.headerBackground = data.local_exchange_header_background;
		this.requiresApproval = data.requires_approval;

		this.primaryColor = data.branding.button_color;
		this.backgroundColor = data.branding.background_color;
		this.headerBackgroundColor = data.branding.header_background_color;
		this.causes = data.causes;
		this.publish = data.publish;
		this.sports = data.sports;
		this.attestations = (data.provider_attestations ?? [])
			.sort((a, b) => a.order - b.order)
			.map((a) => new Attestation(a));
	}
}

class ExchangeSchoolSiteAthlete implements SingleExchangeSchoolSiteAthlete {
	uuid;
	firstName;
	lastName;
	fullName;
	profileImage;
	rateCardsCount;
	teamName;
	socialProfiles;
	links;

	constructor(data: ApiV3.ExchangeSchoolSiteAthlete) {
		this.uuid = data.uuid;
		this.firstName = data.first_name;
		this.lastName = data.last_name;
		this.fullName = `${data.first_name} ${data.last_name}`;
		this.profileImage = data.profile_image;
		this.rateCardsCount = data.rate_cards;
		this.teamName = data.team;
		this.socialProfiles = data.social_profiles.filter((socialProfile) => {
			const includedSocialProviders: SocialMediaPlatformId[] = [
				SOCIAL_MEDIA_PLATFORM_IDS.INSTAGRAM,
				SOCIAL_MEDIA_PLATFORM_IDS.TWITTER,
				SOCIAL_MEDIA_PLATFORM_IDS.TIKTOK,
			];
			return socialProfile.public_profile_url && includedSocialProviders.includes(socialProfile.social_provider);
		}).map((socialProfile) => {
			return {
				url: socialProfile.public_profile_url!,
				platform: SOCIAL_MEDIA_PLATFORMS_MAPPED[socialProfile.social_provider]!,
			};
		});
		this.links = data.links
			? data.links.map((link) => {
				return {
					url: link,
				};
			})
			: [];
	}
}

export {
	ExchangeSchoolSite,
	ExchangeSchoolSiteAthlete,
	type SingleExchangeSchoolAthleteLink,
	type SingleExchangeSchoolSiteAthlete,
};
